/**
 * Source selection: given an IR (whose triple patterns carry `sourceIds`
 * provenance) decide which source(s) answer it. M1 ships the single-source
 * planner — every term resolves to one source. The interface is where real
 * federation (multi-source SERVICE / client-side federation via Comunica, plus
 * ASK/VoID probing) lands in M3, with no change upstream.
 */
import type { QueryIR } from "../ir/types.ts";
import type { SourceRegistry } from "./types.ts";

export interface SourcePlan {
  primarySourceId: string;
  sourceIds: string[];
  federated: boolean;
  rationale: string;
}

export interface FederationPlanner {
  plan(ir: QueryIR, registry: SourceRegistry): SourcePlan;
}

export class SingleSourceFederationPlanner implements FederationPlanner {
  plan(ir: QueryIR, registry: SourceRegistry): SourcePlan {
    const ids = new Set<string>();
    for (const p of ir.patterns) for (const s of p.sourceIds ?? []) ids.add(s);
    const list = [...ids];
    const primary = list[0] ?? registry.defaultSource().id;
    const federated = list.length > 1;
    return {
      primarySourceId: primary,
      sourceIds: list.length ? list : [primary],
      federated,
      rationale: federated
        ? `Terms span ${list.length} sources (${list.join(", ")}); federation is planned for a later milestone — running against "${primary}" for now.`
        : `All terms resolve within "${primary}".`,
    };
  }
}
